import { apiRaw } from 'app/api';
import _isEmpty from 'lodash/isEmpty';
import {
  ICreateDrawingMutation,
  ICreateDrawingResponse,
  IGetDrawingResponse,
  IGetAllDrawingsResponse,
  ICreateBomMutation,
  ICreateBomResponse,
  IGetBomResponse,
  IGetAllBomsResponse,
  ICreateBomItemResponse,
  ICreateBomItemMutation,
} from './types';

export const drawingsAndBomsApi = apiRaw.injectEndpoints({
  endpoints: (build) => ({
    createDrawing: build.mutation<ICreateDrawingResponse, ICreateDrawingMutation>({
      query: (payload) => ({
        url: '/engineering-drawings/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'drawings', id: 'LIST' }],
    }),
    deleteDrawing: build.mutation<void, number>({
      query: (id) => ({
        url: `/engineering-drawings/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'drawings', id: 'LIST' }],
    }),
    getAllDrawings: build.query<
      IGetAllDrawingsResponse,
      { thread?: string | number; limit?: number } | void
    >({
      query: (args: { thread?: string | number; limit?: number } = {}) => {
        const { limit = 2000, ...rest } = args;
        return {
          url: '/engineering-drawings/',
          params: { ...rest, limit },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((d) => ({ type: 'drawings' as const, id: d.id })),
              { type: 'drawings', id: 'LIST' },
            ]
          : [{ type: 'drawings', id: 'LIST' }],
    }),
    getDrawing: build.query<IGetDrawingResponse, number>({
      query: (id) => `/engineering-drawings/${id}/`,
      providesTags: ['drawings'],
    }),
    updateDrawing: build.mutation<IGetDrawingResponse, ICreateDrawingMutation & { id: number }>({
      query: ({ id, ...payload }) => ({
        url: `/engineering-drawings/${id}/`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['drawings'],
    }),
    createBom: build.mutation<ICreateBomResponse, ICreateBomMutation>({
      query: (payload) => ({
        url: '/boms/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['boms'],
    }),
    getAllBoms: build.query<
      IGetAllBomsResponse,
      { thread?: string | number; limit?: number } | void
    >({
      query: (args: { thread?: string | number; limit?: number } = {}) => {
        const { limit = 2000, ...rest } = args;
        return {
          url: '/boms/',
          params: { ...rest, limit },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((b) => ({ type: 'boms' as const, id: b.id })),
              { type: 'boms', id: 'LIST' },
            ]
          : [{ type: 'boms', id: 'LIST' }],
    }),
    getBom: build.query<IGetBomResponse, number>({
      query: (id) => `/boms/${id}/`,
      providesTags: (result, error, id) => [{ type: 'boms', id }],
    }),
    updateBom: build.mutation<IGetBomResponse, ICreateBomMutation & { id: number }>({
      query: ({ id, ...payload }) => ({
        url: `/boms/${id}/`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['boms'],
    }),
    createBomItem: build.mutation<ICreateBomItemResponse, ICreateBomItemMutation>({
      query: (payload) => ({
        url: '/bom-items/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'boms', id: 'LIST' }],
    }),
    updateBomItem: build.mutation<ICreateBomItemResponse, ICreateBomItemMutation & { id: number }>({
      query: ({ id, ...payload }) => ({
        url: `/bom-items/${id}/`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: 'boms', id: 'LIST' }],
    }),
    deleteBomItem: build.mutation<void, number>({
      query: (id) => ({
        url: `/bom-items/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'boms', id: 'LIST' }],
    }),
  }),
});

export const {
  useCreateDrawingMutation,
  useDeleteDrawingMutation,
  useGetAllDrawingsQuery,
  useGetDrawingQuery,
  useUpdateDrawingMutation,
  useCreateBomMutation,
  useUpdateBomMutation,
  useGetAllBomsQuery,
  useGetBomQuery,
  useCreateBomItemMutation,
  useDeleteBomItemMutation,
  useUpdateBomItemMutation,
  useLazyGetBomQuery,
  useLazyGetDrawingQuery,
} = drawingsAndBomsApi;
